import React, { useState, useMemo, useRef, useEffect } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import MenuBar from "./plugins/MenuBar";
import BubbleMenuBar from "./plugins/BubbleMenuBar";
// import TableFloatingMenu from "./plugins/TableControls";
import TableControls from "./plugins/TableControls";
import { getBaseExtensions } from "./plugins/EditorConfig";
import styles from "./styles/QurealRichEditor.module.css";
import { css, Global } from "@emotion/react";
import { size } from "lodash";

const QurealRichEditor = ({
    value = "",
    onChange,
    editorOptions = {},
    menuFeatures = "all",
    menuType = "fixed",
    placeholder = {},
    wrapper_style,
    fontSize = 1,
    lineHeight = 1.5,
    letterSpacing = "normal",
    elementSpacing = 1,
    colorContent,
    colorHeadings,
    fontFamilyHeadings,
    bulletStyle = "disc",
}) => {
    const [darkMode, setDarkMode] = useState(false);
    const renderCountRef = useRef(0);

    renderCountRef.current += 1;

    const toggleTheme = () => setDarkMode((prev) => !prev);

    const extensions = useMemo(
        () => getBaseExtensions({ ...editorOptions, placeholder }),
        [editorOptions, placeholder]
    );

    const editorStyles = useMemo(
        () => css`
            .${styles.tiptap} {
                font-size: ${fontSize}em !important;
            }
            /* Reset all default margins first */
            .${styles.tiptap} * {
                margin-top: 0;
                margin-bottom: 0;
            }

            /* Apply consistent spacing to all block elements */
            .${styles.tiptap} p,
            .${styles.tiptap} ul,
            .${styles.tiptap} ol,
            .${styles.tiptap} blockquote,
            .${styles.tiptap} pre,
            .${styles.tiptap} h1,
            .${styles.tiptap} h2,
            .${styles.tiptap} h3,
            .${styles.tiptap} h4,
            .${styles.tiptap} h5,
            .${styles.tiptap} h6,
            .${styles.tiptap} table,
            .${styles.tiptap} .tableWrapper,
            .${styles.tiptap} img,
            .${styles.tiptap} figure,
            .${styles.tiptap} video,
            .${styles.tiptap} iframe,
            .${styles.tiptap} .embed,
            .${styles.tiptap} [data-youtube-video],
            .${styles.tiptap} [data-vimeo-video],
            .${styles.tiptap} .image-container,
            .${styles.tiptap} hr,
            .${styles.tiptap} .code-block {
                margin-top: ${elementSpacing}em !important;
                margin-bottom: ${elementSpacing}em !important;
            }

            /* Remove top margin from first child to avoid double spacing */
            .${styles.tiptap} > *:first-child {
                margin-top: 0;
            }

            /* Remove bottom margin from last child to avoid extra space */
            .${styles.tiptap} > *:last-child {
                margin-bottom: 0;
            }
            .${styles.tiptap} li p {
                margin-top: 0.25em !important;
                margin-bottom: 0.25em !important;
            }
            .${styles.tiptap} li ul,
            .${styles.tiptap} li ol {
                margin-top: 0.25em !important;
                margin-bottom: 0.25em !important;
            }

            /* Text styles */
            .${styles.tiptap} p,
            .${styles.tiptap} ul,
            .${styles.tiptap} ol,
            .${styles.tiptap} blockquote,
            .${styles.tiptap} pre,
            .${styles.tiptap} pre code,
            .${styles.tiptap} .code-block {
                line-height: ${lineHeight * 100}%;
                letter-spacing: ${letterSpacing};
                color: ${colorContent || "inherit"};
            }

            /* Heading styles */
            .${styles.tiptap} h1,
            .${styles.tiptap} h2,
            .${styles.tiptap} h3,
            .${styles.tiptap} h4,
            .${styles.tiptap} h5,
            .${styles.tiptap} h6 {
                font-family: ${fontFamilyHeadings || "inherit"}!important;
                color: ${colorHeadings || "inherit"}!important;
            }

            /* List specific styles */
            .${styles.tiptap} ul {
                list-style-type: ${bulletStyle};
            }

            /* Code block specific styles */
            .${styles.tiptap} pre {
                line-height: ${lineHeight * 100}% !important;
                white-space: pre-wrap;
            }
        `,
        [
            fontSize,
            lineHeight,
            letterSpacing,
            elementSpacing,
            colorContent,
            colorHeadings,
            fontFamilyHeadings,
            bulletStyle,
        ]
    );

    console.log("Value prop:", value);
    const editor = useEditor({
        extensions,
        content: value || "",
        editorProps: {
            attributes: {
                class: styles.tiptap,
            },
        },
        immediatelyRender: true,
        shouldRerenderOnTransaction: false,
        onUpdate: ({ editor }) => {
            if (onChange) {
                const html = editor.getHTML();
                // Remove empty <p> tags (including those with attributes)
                const cleanedHtml = html.replace(/<p\b[^>]*>\s*<\/p>/gi, "");
                onChange(cleanedHtml);
            }
        },
    });
    editor.on("create", () => {
        console.log(editor.getHTML());
    });

    const dynamicWrapperStyle = useMemo(() => {
        if (!darkMode) return wrapper_style;

        // Merge wrapper_style with dark mode specific styles
        return {
            ...wrapper_style,
            backgroundColor: wrapper_style?.backgroundColor
                ? "#1b201c"
                : undefined,
            color: wrapper_style?.color ? "#e2e8f0" : undefined,
            boxShadow: wrapper_style?.boxShadow
                ? "0 4px 16px rgb(0, 0, 0)"
                : undefined,
        };
    }, [darkMode, wrapper_style]);

    return (
        <>
            <Global styles={editorStyles} />
            <div
                className={`${styles.editorWrapper} ${
                    darkMode ? styles.darkMode : ""
                }`}
            >
                <div
                    className={`${styles.editorContainer} `}
                    style={dynamicWrapperStyle}
                >
                    {menuType === "fixed" && (
                        <MenuBar
                            editor={editor}
                            toggleTheme={toggleTheme}
                            darkMode={darkMode}
                            features={menuFeatures}
                        />
                    )}

                    {editor && (
                        <EditorContent
                            editor={editor}
                            className={styles.editorContent}
                        />
                    )}
                    {/* {editor && <TableControls editor={editor} />} */}

                    {menuType === "bubble" && editor && (
                        <BubbleMenuBar
                            editor={editor}
                            toggleTheme={toggleTheme}
                            darkMode={darkMode}
                            features={menuFeatures}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default React.memo(QurealRichEditor);
